import { useState, useEffect, useMemo, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, X, ShoppingBag, Check, SlidersHorizontal } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_SAMPLES = 10;

const COLOR_FILTERS = [
  { id: 'white', name: 'White', color: '#FFFFFF' },
  { id: 'grey', name: 'Grey', color: '#808080' },
  { id: 'black', name: 'Black', color: '#1A1A1A' },
  { id: 'brown', name: 'Brown', color: '#7B3F00' },
  { id: 'red', name: 'Red', color: '#DC2626' },
  { id: 'orange', name: 'Orange', color: '#EA580C' },
  { id: 'yellow', name: 'Yellow', color: '#EAB308' },
  { id: 'green', name: 'Green', color: '#16A34A' },
  { id: 'blue', name: 'Blue', color: '#2563EB' },
  { id: 'purple', name: 'Purple', color: '#7C3AED' },
  { id: 'pink', name: 'Pink', color: '#EC4899' },
];

const PRICE_FILTERS = [
  { id: 'under60', label: 'Under $60/m', min: 0, max: 60 },
  { id: '60to80', label: '$60 - $80/m', min: 60, max: 80 },
  { id: '80to100', label: '$80 - $100/m', min: 80, max: 100 },
  { id: 'over100', label: '$100+/m', min: 100, max: Infinity },
];

const FilterPanel = ({ ranges, allColors, selectedRanges, toggleRange, selectedColors, toggleColor, selectedTypes, toggleType, selectedPrices, togglePrice, fabricTypes, searchQuery, setSearchQuery, clearAll }) => (
  <div className="space-y-6">
    <div>
      <h3 className="font-semibold text-sm text-gray-700 mb-3">Search</h3>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search fabrics..." className="pl-9 pr-8 text-sm" data-testid="fabric-search" />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="clear-search">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>

    <div>
      <h3 className="font-semibold text-sm text-gray-700 mb-3">Type</h3>
      <div className="flex flex-wrap gap-2">
        {fabricTypes.map(ft => (
          <button
            key={ft.id}
            onClick={() => toggleType(ft.id)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${selectedTypes.has(ft.id) ? 'bg-teal-50 text-teal-700 ring-1 ring-teal-300' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`type-filter-${ft.id}`}
          >
            {ft.name}
            {selectedTypes.has(ft.id) && <X className="w-3 h-3 ml-0.5" />}
          </button>
        ))}
      </div>
    </div>

    <div>
      <h3 className="font-semibold text-sm text-gray-700 mb-3">Colour</h3>
      <div className="flex flex-wrap gap-2">
        {COLOR_FILTERS.map(cf => (
          <button
            key={cf.id}
            onClick={() => toggleColor(cf.id)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors ${selectedColors.has(cf.id) ? 'bg-teal-50 text-teal-700 ring-1 ring-teal-300' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`color-filter-${cf.id}`}
          >
            <span className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: cf.color }} />
            {cf.name}
            {selectedColors.has(cf.id) && <X className="w-3 h-3 ml-0.5" />}
          </button>
        ))}
      </div>
    </div>

    <div>
      <h3 className="font-semibold text-sm text-gray-700 mb-3">Price</h3>
      <div className="flex flex-wrap gap-2">
        {PRICE_FILTERS.map(pf => (
          <button
            key={pf.id}
            onClick={() => togglePrice(pf.id)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${selectedPrices.has(pf.id) ? 'bg-teal-50 text-teal-700 ring-1 ring-teal-300' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`price-filter-${pf.id}`}
          >
            {pf.label}
            {selectedPrices.has(pf.id) && <X className="w-3 h-3 ml-0.5" />}
          </button>
        ))}
      </div>
    </div>

    <div>
      <h3 className="font-semibold text-sm text-gray-700 mb-3">Fabric Range</h3>
      <div className="space-y-1 max-h-[300px] overflow-y-auto">
        {ranges.map(range => (
          <button key={range.id} onClick={() => toggleRange(range.id)} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selectedRanges.has(range.id) ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`} data-testid={`filter-${range.id}`}>
            {range.name} ({(range.colors || []).length})
          </button>
        ))}
      </div>
    </div>

    {(selectedTypes.size > 0 || selectedColors.size > 0 || selectedPrices.size > 0 || selectedRanges.size > 0 || searchQuery.trim()) && (
      <button onClick={clearAll} className="text-xs text-teal-600 hover:underline font-medium" data-testid="clear-all-filters">
        Clear all filters
      </button>
    )}
  </div>
);

const FabricSamplesPage = () => {
  const [ranges, setRanges] = useState([]);
  const [fabricTypes, setFabricTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRanges, setSelectedRanges] = useState(new Set());
  const [selectedColors, setSelectedColors] = useState(new Set());
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [selectedPrices, setSelectedPrices] = useState(new Set());
  const [sortBy, setSortBy] = useState('name');

  const toggleSet = (setter) => (id) => setter(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleRange = toggleSet(setSelectedRanges);
  const toggleColor = toggleSet(setSelectedColors);
  const toggleType = toggleSet(setSelectedTypes);
  const togglePrice = toggleSet(setSelectedPrices);
  const clearAll = () => { setSelectedRanges(new Set()); setSelectedColors(new Set()); setSelectedTypes(new Set()); setSelectedPrices(new Set()); setSearchQuery(''); };
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { sessionId, fetchCart, cart } = useCart();
  const initializedRef = useRef(false);
  // Pulse the floating bar the first time it appears in this session — draws the eye to the CTA.
  const [pulseBar, setPulseBar] = useState(false);
  const hasPulsedRef = useRef(false);
  useEffect(() => {
    if (hasPulsedRef.current) return;
    if (selectedSamples.length > 0) {
      hasPulsedRef.current = true;
      setPulseBar(true);
      const t = setTimeout(() => setPulseBar(false), 1500);
      return () => clearTimeout(t);
    }
  }, [selectedSamples.length]);

  const cartSamples = useMemo(() => {
    const entry = cart.items.find(i => i.item_type === 'fabric-samples');
    return entry?.samples || [];
  }, [cart.items]);

  useEffect(() => {
    if (initializedRef.current || cartSamples.length === 0) return;
    initializedRef.current = true;
    setSelectedSamples(cartSamples.map(s => ({
      id: s.id, name: s.name, rangeName: s.rangeName, imageUrl: s.imageUrl, hex_color: s.hex_color,
    })));
  }, [cartSamples]);

  const hasChanges = useMemo(() => {
    const cartIds = new Set(cartSamples.map(s => s.id));
    const selectedIds = new Set(selectedSamples.map(s => s.id));
    if (cartIds.size !== selectedIds.size) return true;
    for (const id of cartIds) if (!selectedIds.has(id)) return true;
    return false;
  }, [cartSamples, selectedSamples]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/fabric-ranges`).then(r => r.json()),
      fetch(`${API}/api/fabric-types`).then(r => r.json()),
    ])
      .then(([rangesData, typesData]) => {
        setRanges(rangesData);
        setFabricTypes(typesData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Build a map of range id → fabric_types for type filtering
  const rangeTypeMap = useMemo(() => {
    const m = {};
    ranges.forEach(r => { m[r.id] = r.fabric_types || []; });
    return m;
  }, [ranges]);

  const allColors = useMemo(() => {
    return ranges.flatMap(range =>
      (range.colors || []).filter(color => color.in_stock !== false).map(color => ({
        ...color,
        rangeName: range.name,
        rangeId: range.id,
        imageUrl: color.swatch_url || color.image_url,
        pricePerMeter: range.price_per_meter || 0,
      }))
    );
  }, [ranges]);

  const filteredColors = useMemo(() => {
    let result = allColors;
    if (selectedRanges.size > 0) {
      result = result.filter(c => selectedRanges.has(c.rangeId));
    }
    if (selectedColors.size > 0) {
      result = result.filter(c => selectedColors.has(c.color_category));
    }
    if (selectedTypes.size > 0) {
      result = result.filter(c => {
        const types = rangeTypeMap[c.rangeId] || [];
        return types.some(t => selectedTypes.has(t));
      });
    }
    if (selectedPrices.size > 0) {
      const activePriceFilters = PRICE_FILTERS.filter(p => selectedPrices.has(p.id));
      result = result.filter(c => activePriceFilters.some(pf => c.pricePerMeter >= pf.min && c.pricePerMeter < pf.max));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c => c.name.toLowerCase().includes(q) || c.rangeName.toLowerCase().includes(q));
    }
    if (sortBy === 'name') {
      result = [...result].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'range') {
      result = [...result].sort((a, b) => a.rangeName.localeCompare(b.rangeName) || a.name.localeCompare(b.name));
    } else if (sortBy === 'price_low') {
      result = [...result].sort((a, b) => a.pricePerMeter - b.pricePerMeter || a.name.localeCompare(b.name));
    } else if (sortBy === 'price_high') {
      result = [...result].sort((a, b) => b.pricePerMeter - a.pricePerMeter || a.name.localeCompare(b.name));
    }
    return result;
  }, [allColors, selectedRanges, selectedColors, selectedTypes, selectedPrices, searchQuery, sortBy, rangeTypeMap]);

  // Set of visible color IDs for CSS-based hiding (keeps DOM alive for instant filtering)
  const visibleColorIds = useMemo(() => new Set(filteredColors.map(c => c.id)), [filteredColors]);

  /* Infinite-scroll pagination — start with 25 fabrics and add 25 more whenever
   * the user scrolls the sentinel into view. Counter resets on filter/sort
   * changes so the user always starts at the top of the new filtered list. */
  const PAGE_SIZE = 25;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => {
    // Whenever the filtered set changes, snap back to the first page.
    setVisibleCount(PAGE_SIZE);
  }, [filteredColors.length]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, filteredColors.length));
        }
      },
      { rootMargin: '400px 0px' }, // pre-load a bit before sentinel hits the viewport
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [filteredColors.length]);

  const visibleFabrics = useMemo(() => filteredColors.slice(0, visibleCount), [filteredColors, visibleCount]);

  const isSelected = (colorId) => selectedSamples.some(s => s.id === colorId);
  const activeFilterCount = selectedRanges.size + selectedColors.size + selectedTypes.size + selectedPrices.size + (searchQuery.trim() ? 1 : 0);

  const toggleSample = (color) => {
    if (isSelected(color.id)) {
      setSelectedSamples(prev => prev.filter(s => s.id !== color.id));
    } else if (selectedSamples.length >= MAX_SAMPLES) {
      toast.error(`Maximum ${MAX_SAMPLES} fabric samples per order`);
    } else {
      setSelectedSamples(prev => [...prev, color]);
    }
  };

  const syncCart = async () => {
    if (!hasChanges) return;
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/cart/add-samples`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          samples: selectedSamples.map(s => ({ id: s.id, name: s.name, rangeName: s.rangeName, imageUrl: s.imageUrl })),
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update samples');
        return;
      }
      await fetchCart();
      if (selectedSamples.length === 0) {
        toast.success('Fabric samples removed from cart');
      } else {
        toast.success(`${selectedSamples.length} fabric samples saved to cart`);
      }
    } catch {
      toast.error('Failed to update samples');
    } finally {
      setSyncing(false);
    }
  };

  const buttonLabel = cartSamples.length > 0
    ? (selectedSamples.length === 0 ? 'Remove Samples from Cart' : 'Update Samples in Cart')
    : 'Add Free Samples to Cart';

  const filterProps = { ranges, allColors, selectedRanges, toggleRange, selectedColors, toggleColor, selectedTypes, toggleType, selectedPrices, togglePrice, fabricTypes, searchQuery, setSearchQuery, clearAll };

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-[#FAFAF8]" data-testid="fabric-samples-page">
      <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight">Fabric Samples</h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">
            Order up to {MAX_SAMPLES} free fabric samples to see and feel our materials before you buy. 
          </p>
        </div>
      </section>

      <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12 py-8 pb-32 sm:pb-28">
        {/* Selected samples bar — floats at bottom once samples are selected so
            users don't have to scroll back up to reach "Add to cart". */}
        {(selectedSamples.length > 0 || (cartSamples.length > 0 && hasChanges)) && (
          <div
            className={`fixed inset-x-3 bottom-3 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:bottom-6 sm:w-full sm:max-w-2xl z-40 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl p-3 sm:p-4 shadow-xl ${pulseBar ? 'animate-bar-entrance' : ''} ${selectedSamples.length === 0 && !hasChanges ? 'hidden' : ''}`}
            data-testid="samples-bar"
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-semibold text-gray-700">{selectedSamples.length}/{MAX_SAMPLES} selected</span>
                <div className="hidden sm:flex gap-1.5 flex-wrap">
                  {selectedSamples.map(s => (
                    <button key={s.id} onClick={() => toggleSample(s)} className="flex items-center gap-1 bg-gray-100 rounded-full pl-1 pr-2 py-0.5 text-xs text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors" data-testid={`remove-sample-${s.id}`}>
                      <span className="w-4 h-4 rounded-full border" style={{ backgroundColor: s.hex_color || '#ccc' }}></span>
                      <span className="max-w-[140px] truncate">{s.rangeName} - {s.name}</span>
                      <X className="w-3 h-3" />
                    </button>
                  ))}
                </div>
              </div>
              {hasChanges && (
                <Button onClick={syncCart} disabled={syncing} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full text-xs sm:text-sm" data-testid="add-samples-to-cart">
                  {syncing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShoppingBag className="w-4 h-4 mr-1.5" />}
                  {buttonLabel}
                </Button>
              )}
              {!hasChanges && cartSamples.length > 0 && (
                <span className="text-xs text-teal-600 font-medium">Saved in cart</span>
              )}
            </div>
          </div>
        )}

        {/* Toolbar: Filters button + Sort dropdown */}
        <div className="flex items-center justify-between mb-6 gap-3">
          <div className="flex items-center gap-3">
            {/* Mobile filter button (Sheet) */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="outline" className="rounded-full gap-2 text-sm" data-testid="filters-button-mobile">
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="bg-teal-500 text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center">{activeFilterCount}</span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 bg-white overflow-y-auto" data-testid="filters-sheet">
                <div className="mt-8">
                  <h2 className="text-lg font-display font-semibold text-[#1A1A1A] mb-6">Filters</h2>
                  <FilterPanel {...filterProps} />
                  <Button onClick={() => setFiltersOpen(false)} className="w-full mt-6 bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                    Show Results ({filteredColors.length})
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <p className="text-sm text-gray-500">{filteredColors.length} fabric{filteredColors.length !== 1 ? 's' : ''}</p>
          </div>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-32 sm:w-36 h-9 text-xs sm:text-sm rounded-full" data-testid="sort-dropdown">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name A-Z</SelectItem>
              <SelectItem value="range">By Range</SelectItem>
              <SelectItem value="price_low">Price: Low</SelectItem>
              <SelectItem value="price_high">Price: High</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop sidebar — hidden on mobile */}
          <aside className="hidden lg:block w-64 flex-shrink-0" data-testid="fabric-filters">
            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm sticky top-[122px]">
              <FilterPanel {...filterProps} />
            </div>
          </aside>

          {/* Fabric grid */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-7 w-7 animate-spin text-teal-500" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4" data-testid="fabric-grid">
                  {visibleFabrics.map(color => {
                    const selected = isSelected(color.id);
                    return (
                      <button
                        key={color.id}
                        onClick={() => toggleSample(color)}
                        className={`group relative bg-white rounded-xl border-2 overflow-hidden transition-all ${selected ? 'border-teal-500 ring-2 ring-teal-200' : 'border-gray-100 hover:border-gray-300'}`}
                        data-testid={`fabric-swatch-${color.id}`}
                      >
                        <div className="aspect-square relative">
                          {color.imageUrl ? (
                            <img src={color.imageUrl} alt={color.name} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full" style={{ backgroundColor: color.hex_color || '#ddd' }}></div>
                          )}
                          {selected && (
                            <div className="absolute inset-0 bg-teal-500/20 flex items-center justify-center">
                              <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center">
                                <Check className="w-5 h-5 text-white" />
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="p-2 text-left">
                          <p className="text-xs font-medium text-gray-800 truncate">{color.name}</p>
                          <div className="flex items-center justify-between">
                            <p className="text-[10px] text-gray-500 truncate">{color.rangeName}</p>
                            {color.pricePerMeter > 0 && <p className="text-[10px] font-medium text-teal-600">${color.pricePerMeter}/m</p>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* Infinite-scroll sentinel — when this scrolls into view, load the next batch. */}
                {visibleCount < filteredColors.length && (
                  <div ref={sentinelRef} className="flex items-center justify-center py-6" data-testid="fabric-load-more-sentinel">
                    <Loader2 className="h-5 w-5 animate-spin text-teal-500" />
                  </div>
                )}
                {visibleColorIds.size === 0 && (
                  <p className="text-center text-gray-400 py-16">No fabrics match your filters.</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FabricSamplesPage;
